import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Space, Tag } from "antd";
import {
    CalendarOutlined,
    CheckCircleOutlined,
    NotificationOutlined,
    StopOutlined,
} from "@ant-design/icons";
import { AxiosError } from "axios";
import Highlighter from "react-highlight-words";

import { INotice } from "../../interfaces";
import NoticeService from "../../services/http/services/NoticeService";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import useAuth from "../../hooks/useAuth";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableNoticeData extends INotice {
    key: number;
}

export default function useNotice() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<Array<ITableNoticeData>>([]);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isLoadingUpload, setIsLoadingUpload] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [noticeBeingDeleted, setNoticeBeingDeleted] = useState<ITableNoticeData | null>(null);
    const [isUpdateNoticeModalVisible, setIsUpdateNoticeModalVisible] = useState<boolean>(false);
    const [noticeBeingUpdated, setNoticeBeingUpdated] = useState<ITableNoticeData | null>(null);

    const [searchText, setSearchText] = useState<string>("");
    const [filterStatus, setFilterStatus] = useState<"ALL" | "ACTIVE" | "INACTIVE">("ALL");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const loadNotice = useCallback(
        async (signal?: AbortSignal) => {
            if (!activeCity?.id) return;
            try {
                const controller = signal ? undefined : new AbortController();
                const noticeList = await NoticeService.listCityNotices(
                    activeCity.id,
                    signal || controller!.signal
                );
                const mappedData: ITableNoticeData[] = (noticeList || []).map((notice: INotice) => ({
                    ...notice,
                    key: notice.id || Math.random(),
                }));
                setTableData(mappedData);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }
                EmitError(err, true, "notice_load_error");
            } finally {
                setIsLoading(false);
            }
        },
        [activeCity]
    );

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);
        loadNotice(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadNotice]);

    const handleClickCreateNewNotice = useCallback(() => {
        navigate("/avisos/criar");
    }, [navigate]);

    const handleClickEditNotice = useCallback(
        (data: INotice) => {
            navigate(`/avisos/editar/${data.id}`);
        },
        [navigate]
    );

    const handleDeleteNotice = useCallback((data: ITableNoticeData) => {
        setNoticeBeingDeleted(data);
        setIsDeleteModalVisible(true);
    }, []);

    const handleUpdateNotice = useCallback((data: ITableNoticeData) => {
        setNoticeBeingUpdated(data);
        setIsUpdateNoticeModalVisible(true);
    }, []);

    const handleConfirmDeleteNotice = useCallback(async () => {
        if (!noticeBeingDeleted?.id) return;
        try {
            setIsLoadingDelete(true);
            await NoticeService.deleteNotice(noticeBeingDeleted.id);
            EmitSuccessAlert("Aviso removido com sucesso!");
            setTableData((prev) => prev.filter((item) => item.id !== noticeBeingDeleted.id));
            setIsDeleteModalVisible(false);
            setNoticeBeingDeleted(null);
        } catch (err) {
            EmitError(err, true, "notice_delete_error");
        } finally {
            setIsLoadingDelete(false);
        }
    }, [noticeBeingDeleted]);

    const handleCancelDeleteNotice = useCallback(() => {
        setIsDeleteModalVisible(false);
        setNoticeBeingDeleted(null);
    }, []);

    const handleConfirmUpdateNotice = useCallback(async () => {
        if (!noticeBeingUpdated?.id) return;
        try {
            setIsLoadingUpload(true);
            const isCurrentlyActive =
                typeof noticeBeingUpdated.status === "string"
                    ? noticeBeingUpdated.status === "true"
                    : Boolean(noticeBeingUpdated.status);
            const newStatus = String(!isCurrentlyActive);

            const noticeUpdated = await NoticeService.updateNotice(noticeBeingUpdated.id, {
                status: newStatus,
            });

            EmitSuccessAlert("Status do aviso atualizado com sucesso!");
            setTableData((prev) =>
                prev.map((item) =>
                    item.id === noticeUpdated.id
                        ? { ...item, ...noticeUpdated, key: item.key }
                        : item
                )
            );
            setIsUpdateNoticeModalVisible(false);
            setNoticeBeingUpdated(null);
        } catch (err) {
            EmitError(err, true, "notice_update_status_error");
        } finally {
            setIsLoadingUpload(false);
        }
    }, [noticeBeingUpdated]);

    const handleCancelUpdateNotice = useCallback(() => {
        setIsUpdateNoticeModalVisible(false);
        setNoticeBeingUpdated(null);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setSearchText("");
        setFilterStatus("ALL");
        setCurrentPage(1);
    }, []);

    // Filter table data
    const filteredData = useMemo(() => {
        let result = tableData;

        if (searchText && searchText.trim()) {
            const query = searchText.toLowerCase().trim();
            result = result.filter((notice) => notice.title?.toLowerCase().includes(query));
        }

        if (filterStatus !== "ALL") {
            result = result.filter((notice) => {
                const isActive =
                    typeof notice.status === "string"
                        ? notice.status === "true"
                        : Boolean(notice.status);
                return filterStatus === "ACTIVE" ? isActive : !isActive;
            });
        }

        return result;
    }, [tableData, searchText, filterStatus]);

    // Active Filter Items
    const activeFilterItems = useMemo((): IActiveFilterItem[] => {
        const items: IActiveFilterItem[] = [];

        if (searchText.trim()) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${searchText}"`,
                onClear: () => {
                    setSearchText("");
                    setCurrentPage(1);
                },
            });
        }

        if (filterStatus !== "ALL") {
            items.push({
                key: "status",
                label: "Status",
                value: filterStatus === "ACTIVE" ? "Ativos" : "Inativos",
                onClear: () => {
                    setFilterStatus("ALL");
                    setCurrentPage(1);
                },
            });
        }

        return items;
    }, [searchText, filterStatus]);

    const columns = useMemo(
        () => [
            {
                title: "Título do Aviso",
                dataIndex: "title",
                key: "title",
                sorter: (a: ITableNoticeData, b: ITableNoticeData) =>
                    (a.title || "").localeCompare(b.title || ""),
                render: (text: string) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: "rgba(250, 140, 22, 0.12)",
                                color: "#fa8c16",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                                flexShrink: 0,
                            }}
                        >
                            <NotificationOutlined />
                        </div>
                        <span style={{ fontWeight: 600, fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: "#ffc069", padding: "0 2px" }}
                                searchWords={searchText ? [searchText] : []}
                                autoEscape
                                textToHighlight={text || "--"}
                            />
                        </span>
                    </div>
                ),
            },
            {
                title: "Data de Cadastro",
                dataIndex: "created_at",
                key: "created_at",
                width: 200,
                sorter: (a: ITableNoticeData, b: ITableNoticeData) =>
                    new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime(),
                render: (criado: string) => (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, color: isDark ? "#8c8c8c" : "#64748b", fontSize: 12.5 }}>
                        <CalendarOutlined style={{ color: "#fa8c16" }} />
                        <span>{criado ? formatDateStringToDateAndTime(criado) : "--"}</span>
                    </div>
                ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                width: 120,
                align: "center" as const,
                sorter: (a: ITableNoticeData, b: ITableNoticeData) => {
                    const aActive =
                        typeof a.status === "string" ? a.status === "true" : Boolean(a.status);
                    const bActive =
                        typeof b.status === "string" ? b.status === "true" : Boolean(b.status);
                    return (aActive ? 1 : 0) - (bActive ? 1 : 0);
                },
                render: (status: any) => {
                    const isActive =
                        typeof status === "string" ? status === "true" : Boolean(status);
                    return isActive ? (
                        <Tag
                            color="success"
                            style={{
                                borderRadius: 10,
                                margin: 0,
                                fontWeight: 600,
                                fontSize: 11.5,
                            }}
                        >
                            Ativo
                        </Tag>
                    ) : (
                        <Tag
                            color="error"
                            style={{
                                borderRadius: 10,
                                margin: 0,
                                fontWeight: 600,
                                fontSize: 11.5,
                            }}
                        >
                            Inativo
                        </Tag>
                    );
                },
            },
            {
                title: "Ações",
                key: "actions",
                width: 120,
                align: "center" as const,
                render: (_: any, record: ITableNoticeData) => {
                    const isActive =
                        typeof record.status === "string"
                            ? record.status === "true"
                            : Boolean(record.status);
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Space size={6}>
                                <TableActionButton
                                    action="edit"
                                    tooltip="Editar aviso"
                                    onClick={() => handleClickEditNotice(record)}
                                />
                                <TableActionButton
                                    action="custom"
                                    icon={
                                        isActive ? (
                                            <StopOutlined style={{ fontSize: 14, color: "#ff4d4f" }} />
                                        ) : (
                                            <CheckCircleOutlined style={{ fontSize: 14, color: "#52c41a" }} />
                                        )
                                    }
                                    tooltip={isActive ? "Inativar aviso" : "Ativar aviso"}
                                    bgColor={
                                        isActive
                                            ? isDark
                                                ? "rgba(255, 77, 79, 0.12)"
                                                : "#fff1f0"
                                            : isDark
                                            ? "rgba(82, 196, 26, 0.12)"
                                            : "#f6ffed"
                                    }
                                    onClick={() => handleUpdateNotice(record)}
                                />
                                <TableActionButton
                                    action="delete"
                                    tooltip="Excluir aviso"
                                    onClick={() => handleDeleteNotice(record)}
                                />
                            </Space>
                        </div>
                    );
                },
            },
        ],
        [isDark, searchText, handleClickEditNotice, handleUpdateNotice, handleDeleteNotice]
    );

    return {
        isLoading,
        isLoadingDelete,
        isLoadingUpload,
        isDeleteModalVisible,
        noticeBeingDeleted,
        isUpdateNoticeModalVisible,
        noticeBeingUpdated,
        columns,
        tableData,
        filteredData,
        searchText,
        setSearchText,
        filterStatus,
        setFilterStatus,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        activeFilterItems,
        handleClickCreateNewNotice,
        handleClickEditNotice,
        handleDeleteNotice,
        handleConfirmDeleteNotice,
        handleCancelDeleteNotice,
        handleUpdateNotice,
        handleConfirmUpdateNotice,
        handleCancelUpdateNotice,
        handleClearAllFilters,
    };
}
